import { Component, OnInit, inject } from '@angular/core';
import { RouterLink, Router, ActivatedRoute, Params } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../../../services/api.service';
import { ToastrService } from 'ngx-toastr';
import { ShortenPipe } from '../../../../pipes/shorten.pipe';
import * as _ from 'lodash';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Creative, CreativeMetrics } from '../../../../interfaces/performance-insights';
import { PerformanceInsightsService } from '../../../../services/performance-insights.service';
import { NgbDatepickerModule, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-performance-insights-creatives',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ShortenPipe,
    NgbTooltipModule,
    NgbDatepickerModule
  ],
  templateUrl: './performance-insights-creatives.component.html',
  styleUrl: './performance-insights-creatives.component.css'
})
export class PerformanceInsightsCreativesComponent implements OnInit {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  apiService: ApiService = inject(ApiService);
  toastrService: ToastrService = inject(ToastrService);
  performanceInsightsService: PerformanceInsightsService = inject(PerformanceInsightsService);

  // User Authentication
  authenticatedUser = this.authService.authenticatedUser;

  // Component-specific variables
  accountId: string = '';


  views = {
    creatives: {
      title: 'All Creatives',
      description: 'View all creatives in the ad account',
      show: false,
      creatives: [] as Creative[],
      loadingCreatives: false,
      orderBy: 'id',
      orderDir: 'desc',
      dateStart: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Yesterday
    },
    creativesTopROAS: {
      title: 'Top Creatives',
      description: 'View the Top 20 creatives by ROAS',
      show: false,
      creatives: [] as Creative[],
      loadingCreativesTopROAS: false,
      dateStart: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Yesterday
      amount_spent_min: 500,
      amount_spent_max: null
    },
    creativesLowestROAS: {
      title: 'Lowest Creatives',
      description: 'View the Lowest 20 creatives by ROAS',
      show: false,
      creatives: [] as Creative[],
      loadingCreativesLowestROAS: false,
      dateStart: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Yesterday
      amount_spent_min: 500,
      amount_spent_max: null
    },
    creativesSource: {
      title: 'Creative Source',
      description: 'View a creative source\'s creatives',
      source: 'WEBPROFITS',
      show: false,
      creatives: [] as Creative[],
      loadingCreativesSource: false,
      dateStart: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Yesterday
    }
  } 

  //TODO: Get this from the database
  creativeSources = ['ADSTR', 'BLACKBOARD', 'EXPOSURE', 'HUMANN', 'WEBPROFITS'];


  ngOnInit(): void {
    
    // Check that user is authenticated (should be the case if this path is protected with AuthGuardService in app.routes.ts)
    this.authService.checkUserSignedIn().then(checkSign => {
      if (!checkSign) {
        // User is NOT signed in => Call AuthService.signOut() to redirect to Sign in page
        this.authService.signOut();
      } else {
        // User is signed in
        // Get Ad Account ID from URL params
        this.activatedRoute.params.subscribe((params: Params) => {
          this.accountId = params['id'];
          console.log(`Loading Ad Account#: ${this.accountId}`);

          // Show the creatives view by default
          this.showViewCreativesTopROAS();          

          // Check if the user has access to the Ad Accounts section & this specific ad account
          this.authService.checkUserHasAccessToAdAccounts().then(adAccounts => {
            if(adAccounts === null || !adAccounts.some(adAccount => adAccount.id === this.accountId)) {
              // User does not have access to the account (or the user has no ad accounts at all)
              console.log("User does not have access to this ad account");
              this.toastrService.error("You do not have access to this Ad Account", "Access Denied");
              this.router.navigate(['/']);
            } else {
              // User has access to the account
              console.log("User has access to this ad account");

            }        
          });
        });
      }  
    });
  }


  // Get all creatives
  async getCreatives() {
      return new Promise<Creative[]>((resolve, reject) => {
          console.log("getCreatives()");
          let creatives: Creative[] = [];
          this.views.creatives.loadingCreatives = true;
          this.performanceInsightsService.getCreatives(this.accountId, { limit: 100, date_start: this.views.creatives.dateStart, date_end: this.views.creatives.dateEnd, order_by: this.views.creatives.orderBy, order_dir: this.views.creatives.orderDir })
          .then(response => {
              console.log("[PerformanceInsightsComponent] getCreatives() API response", response);
              // Get creatives from response
              if (response && response.length > 0) {
                  creatives = response.map((creative: Creative) => {
                      return {
                          id: creative.id,
                          image_url: creative.image_url,
                          creative_source: creative.creative_source,
                          creator: creative.creator,
                          metrics: creative.metrics
                      };
                  });
              } else {
                  console.log("[PerformanceInsightsComponent] getCreatives() No creatives found");
              }
              this.views.creatives.loadingCreatives = false;
              resolve(creatives);
          })
          .catch(error => {
              console.log("[PerformanceInsightsComponent] getCreatives() API error", error);
              this.views.creatives.loadingCreatives = false;
              reject(error.name);
          });

      });
  }  

  // Get top creatives by ROAS
  async getCreativesTopROAS() {
      return new Promise<Creative[]>((resolve, reject) => {
          console.log("getCreativesTopROAS()");
          let creatives: Creative[] = [];
          this.views.creativesTopROAS.loadingCreativesTopROAS = true;
          let optionsGetCreatives: any = { 
            limit: 20, 
            date_start: this.views.creativesTopROAS.dateStart, 
            date_end: this.views.creativesTopROAS.dateEnd, 
            amount_spent_min: this.views.creativesTopROAS.amount_spent_min,
            order_by: 'roas', 
            order_dir: 'desc' 
          };
          if (this.views.creativesTopROAS.amount_spent_max !== null) {
            optionsGetCreatives.amount_spent_max = this.views.creativesTopROAS.amount_spent_max;
          }
          this.performanceInsightsService.getCreatives(this.accountId, optionsGetCreatives)
          .then(response => {
              console.log("[PerformanceInsightsComponent] getCreativesTopROAS() API response", response);
              // Get creatives from response
              if (response && response.length > 0) {
                  creatives = response.map((creative: Creative) => {
                      return {
                          id: creative.id,
                          image_url: creative.image_url,
                          creative_source: creative.creative_source,
                          creator: creative.creator,
                          metrics: creative.metrics
                      };
                  });
              } else {
                  console.log("[PerformanceInsightsComponent] getCreativesTopROAS() No creatives found");
              }
              this.views.creativesTopROAS.loadingCreativesTopROAS = false;
              resolve(creatives);
          })
          .catch(error => {
              console.log("[PerformanceInsightsComponent] getCreativesTopROAS() API error", error);
              this.views.creativesTopROAS.loadingCreativesTopROAS = false;
              reject(error.name);
          });
      });
  }

  // Get Lowest creatives by ROAS
  async getCreativesLowestROAS() {
      return new Promise<Creative[]>((resolve, reject) => {
          console.log("getCreativesLowestROAS()");
          let creatives: Creative[] = [];
          this.views.creativesLowestROAS.loadingCreativesLowestROAS = true;
          let optionsGetCreatives: any = { 
            limit: 20, 
            date_start: this.views.creativesLowestROAS.dateStart, 
            date_end: this.views.creativesLowestROAS.dateEnd, 
            amount_spent_min: this.views.creativesLowestROAS.amount_spent_min,
            order_by: 'roas', 
            order_dir: 'asc' 
          };
          if (this.views.creativesLowestROAS.amount_spent_max !== null) {
            optionsGetCreatives.amount_spent_max = this.views.creativesLowestROAS.amount_spent_max;
          }
          this.performanceInsightsService.getCreatives(this.accountId, optionsGetCreatives)
          .then(response => {
              console.log("[PerformanceInsightsComponent] getCreativesLowestROAS() API response", response);
              // Get creatives from response
              if (response && response.length > 0) {
                  creatives = response.map((creative: Creative) => {
                      return {
                          id: creative.id,
                          image_url: creative.image_url,
                          creative_source: creative.creative_source,
                          creator: creative.creator,
                          metrics: creative.metrics
                      };
                  });
              } else {
                  console.log("[PerformanceInsightsComponent] getCreativesLowestROAS() No creatives found");
              }
              this.views.creativesLowestROAS.loadingCreativesLowestROAS = false;
              resolve(creatives);
          })
          .catch(error => {
              console.log("[PerformanceInsightsComponent] getCreativesLowestROAS() API error", error);
              this.views.creativesLowestROAS.loadingCreativesLowestROAS = false;
              reject(error.name);
          });
      });
  }

  // Get creatives by source
  async getCreativesSource(creative_source: string) {
      return new Promise<Creative[]>((resolve, reject) => {
          console.log("getCreativesSource()");
          let creatives: Creative[] = [];
          this.views.creativesSource.loadingCreativesSource = true;
          this.performanceInsightsService.getCreatives(this.accountId, { limit: 100, date_start: this.views.creativesSource.dateStart, date_end: this.views.creativesSource.dateEnd, creative_source: creative_source })
          .then(response => {
            console.log("[PerformanceInsightsComponent] getCreativesSource() API response", response);
            // Get creatives from response
            if (response && response.length > 0) {
                creatives = response.map((creative: Creative) => {
                    return {
                        id: creative.id,
                        image_url: creative.image_url,
                        creative_source: creative.creative_source,
                        creator: creative.creator,
                        metrics: creative.metrics
                    };
                });
            } else {
                console.log("[PerformanceInsightsComponent] getCreativesSource() No creatives found");
            }
            this.views.creativesSource.loadingCreativesSource = false;
            resolve(creatives);
        })
        .catch(error => {
            console.log("[PerformanceInsightsComponent] getCreativesSource() API error", error);
            this.views.creativesSource.loadingCreativesSource = false;
            reject(error.name);
        });
    });
  }

  changeOrderBy(view: string, orderBy: string) {
    console.log("changeOrderBy()", view, orderBy);
    if(view === 'creatives') {
      this.views.creatives.orderBy = orderBy;
      this.views.creatives.orderDir = this.views.creatives.orderDir === 'asc' ? 'desc' : 'asc';
      this.views.creatives.creatives = [];
      this.getCreatives().then(creatives => {
        console.log("Creatives", creatives);
        this.views.creatives.creatives = creatives;
      });
    }

  }

  showViewCreatives() {
    this.views.creatives.show = true;
    this.views.creativesTopROAS.show = false;   
    this.views.creativesLowestROAS.show = false;   
    this.views.creativesSource.show = false;
    this.views.creatives.creatives = [];
    this.getCreatives().then(creatives => {
      console.log("Creatives", creatives);
      this.views.creatives.creatives = creatives;
    });
  }

  showViewCreativesTopROAS() {
    this.views.creatives.show = false;
    this.views.creativesTopROAS.show = true;
    this.views.creativesLowestROAS.show = false;
    this.views.creativesSource.show = false;
    this.views.creativesTopROAS.creatives = [];
    this.getCreativesTopROAS().then(creatives => {
      console.log("Creatives Top ROAS", creatives);
      this.views.creativesTopROAS.creatives = creatives;
    });
  }

  showViewCreativesLowestROAS() {
    this.views.creatives.show = false;
    this.views.creativesTopROAS.show = false;
    this.views.creativesLowestROAS.show = true;
    this.views.creativesSource.show = false;
    this.views.creativesLowestROAS.creatives = [];
    this.getCreativesLowestROAS().then(creatives => {
      console.log("Creatives Lowest ROAS", creatives);
      this.views.creativesLowestROAS.creatives = creatives;
    });
  }

  showViewCreativesSource(creative_source: string) {
    this.views.creatives.show = false;
    this.views.creativesTopROAS.show = false;
    this.views.creativesLowestROAS.show = false;
    this.views.creativesSource.show = true;
    this.views.creativesSource.creatives = [];
    this.getCreativesSource(creative_source).then(creatives => {
      console.log("Creatives Source", creatives);
      this.views.creativesSource.creatives = creatives;
    });
  }

  changeView(view: 'creatives' | 'creativesTopROAS' | 'creativesLowestROAS' | 'creativesSource') {
    console.log("changeView()", view);
    if(view === 'creatives') {
      this.showViewCreatives();
    } else if(view === 'creativesTopROAS') {
      this.showViewCreativesTopROAS();
    } else if(view === 'creativesLowestROAS') {
      this.showViewCreativesLowestROAS();
    } else if(view === 'creativesSource') {
      this.showViewCreativesSource(this.views.creativesSource.source);
    }
  }

  changeDate(view: string, date: string, ngbDate: NgbDateStruct) {
    console.log("changeDate()", view, date, ngbDate);
    const year = ngbDate.year;
    const month = String(ngbDate.month).padStart(2, '0');
    const day = String(ngbDate.day).padStart(2, '0');
    let newDate = `${year}-${month}-${day}`;
    switch(view) {
      case 'creatives':
        console.log("Change date for creatives", date, newDate);
        if(date === 'start') {
          this.views.creatives.dateStart = newDate;
        } else if(date === 'end') {
          this.views.creatives.dateEnd = newDate;
        } else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.creatives.creatives = [];
        this.getCreatives().then(creatives => {
          console.log("Creatives", creatives);
          this.views.creatives.creatives = creatives;
        });
        break;    
      case 'creativesTopROAS':
        console.log("Change date for creativesTopROAS", date, newDate);
        if(date === 'start') {
          this.views.creativesTopROAS.dateStart = newDate;
        }
        else if(date === 'end') {
          this.views.creativesTopROAS.dateEnd = newDate;
        }
        else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.creativesTopROAS.creatives = [];
        this.getCreativesTopROAS().then(creatives => {
          console.log("Creatives Top ROAS", creatives);
          this.views.creativesTopROAS.creatives = creatives;
        });
        break;
      case 'creativesLowestROAS':
        console.log("Change date for creativesLowestROAS", date, newDate);
        if(date === 'start') {
          this.views.creativesLowestROAS.dateStart = newDate;
        }
        else if(date === 'end') {
          this.views.creativesLowestROAS.dateEnd = newDate;
        }
        else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.creativesLowestROAS.creatives = [];
        this.getCreativesLowestROAS().then(creatives => {
          console.log("Creatives Lowest ROAS", creatives);
          this.views.creativesLowestROAS.creatives = creatives;
        });
        break;
      case 'creativesSource':
        console.log("Change date for creativesSource", date, newDate);
        if(date === 'start') {
          this.views.creativesSource.dateStart = newDate;
        }
        else if(date === 'end') {
          this.views.creativesSource.dateEnd = newDate;
        }
        else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.creativesSource.creatives = [];
        this.getCreativesSource(this.views.creativesSource.source).then(creatives => {
          console.log("Creatives Source", creatives);
          this.views.creativesSource.creatives = creatives;
        });
        break;    
    }
    
  }

  changeFilter(view: string, filter: string, value: any) {
    console.log("changeFilter()", view, filter, value);
    if(view === 'creativesSource') {
      this.views.creativesSource.source = value;
      this.views.creativesSource.creatives = [];
      this.getCreativesSource(this.views.creativesSource.source).then(creatives => {
        console.log("Creatives", creatives);
        this.views.creativesSource.creatives = creatives;
      });
    } else if(view == 'creativesTopROAS') {
      this.views.creativesTopROAS.creatives = [];
      this.getCreativesTopROAS().then(creatives => {
        console.log("Creatives", creatives);
        this.views.creativesTopROAS.creatives = creatives;
      });
    } else if(view == 'creativesLowestROAS') {
      this.views.creativesLowestROAS.creatives = [];
      this.getCreativesLowestROAS().then(creatives => {
        console.log("Creatives", creatives);
        this.views.creativesLowestROAS.creatives = creatives;
      });
    }
  }


}
